import { useEffect } from "react";

import { useSearchParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";

import { FlowVersion } from "src/api/flowTypes";
import { toastActions } from "src/design-system/Toast/utils";
import {
  LeftPaneOptions,
  SchemaOptions,
  URLKeys,
  useOpenSchemaEditor,
} from "src/router/SearchParams";
import { getTemplateMetadata } from "src/schema/schemaEditor/useCaseTemplateInputSchemaValidation";

export const ARCHIVED_TOAST_ID = "case-template-version-archived";
export const useShowCaseTemplateVersionArchivedToast = (
  isArchived: boolean,
) => {
  const openSchemaEditor = useOpenSchemaEditor();
  useEffect(() => {
    if (isArchived) {
      toastActions.failure({
        id: ARCHIVED_TOAST_ID,
        title: "Case template has been archived",
        duration: null,
        description:
          "The case template version associated with this Decision Flow has been archived, please check the logic of your Decision Flow",
        actionText: "View schema",
        onActionClick: () => {
          openSchemaEditor(SchemaOptions.Input);
        },
      });
    }
  }, [isArchived, openSchemaEditor]);
};

export const SHOW_REFRESH_TOOLTIP_ON_OPEN = "show-refresh-tooltip";
export const CHANGED_TOAST_ID = "case-template-version-changed";

const DISMISSED_STORAGE_KEY = "case-template-toast-dismissed";

type DismissedRecord = Record<string, boolean>;

export const useShowCaseTemplateVersionChangedToast = (
  isChanged: boolean,
  inputSchema: FlowVersion["input_schema"],
  flowVersionId: string,
) => {
  const [_, setSearchParams] = useSearchParams();

  const { versionId, etag } = getTemplateMetadata(inputSchema);

  const entryKey =
    versionId && etag ? `${flowVersionId}:${versionId}:${etag}` : null;

  const [dismissedRecord, setDismissedRecord] =
    useLocalStorage<DismissedRecord>(DISMISSED_STORAGE_KEY, {});

  const dismissed = entryKey ? dismissedRecord[entryKey] === true : false;

  useEffect(() => {
    if (!isChanged || dismissed || !entryKey) return;

    toastActions.default({
      id: CHANGED_TOAST_ID,
      title: "Case template has been changed",
      duration: null,
      description:
        "The case template version associated with this Decision Flow has been modified, please check the logic of your Decision Flow",
      actionText: "View schema",
      onActionClick: () => {
        setSearchParams((prev) => {
          prev.set(URLKeys.LeftPane, LeftPaneOptions.Schema);
          prev.set(URLKeys.Schema, SchemaOptions.Input);
          prev.set(SHOW_REFRESH_TOOLTIP_ON_OPEN, "true");
          return prev;
        });
      },
      onDismiss: () =>
        setDismissedRecord((prev) => ({ ...prev, [entryKey]: true })),
    });
  }, [isChanged, dismissed, entryKey, setDismissedRecord, setSearchParams]);
};
